import { AlertCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import React, { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { toast } from "../lib/toast";

import { apiClient } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";

interface VeracrossFormData {
  clientId: string;
  clientSecret: string;
  schoolRoute: string;
}

const VeracrossCredentialsPage: FC = () => {
  const navigate = useNavigate();
  const { encryptedUserId, isLoading: authLoading } = useAuth();


  // Check for existing credentials on load
  useEffect(() => {
    const checkExistingCredentials = async () => {
      if (authLoading) {
        return;
      }

      if (!encryptedUserId) {
        setIsCheckingExisting(false);
        return;
      }

      try {
        // First check if user is already fully onboarded
        const onboardingResponse = await apiClient.checkUserOnboardingStatus(encryptedUserId);
        
        if (onboardingResponse.success && onboardingResponse.hasBothTokens) {
          navigate(
            `/applicant-field-mapping?userId=${encodeURIComponent(encryptedUserId)}`
          );
          return;
        }
        
        const response = await apiClient.checkExistingVeracrossCredentials(encryptedUserId);
        
        if (response.success) {
          setExistingCredentials(response);
          
          if (response.hasExistingUser) {
            if (response.hasValidToken) {
              if (response.tokenRefreshed) {
                toast({
                  title: "Token Refreshed",
                  description: "Your Veracross token was automatically refreshed and is now valid.",
                });
              }
              
              setShowExistingCredentialsDialog(true);
            } else {
              toast({
                title: "Token Expired",
                description: "Your Veracross token has expired. You can use existing credentials or add new ones.",
              });
              
              setShowExistingCredentialsDialog(true);
            }
          } else if (response.needsReauth) {
            toast({
              title: "Re-authentication Required",
              description: "Your Veracross token has expired and cannot be refreshed. Please enter your credentials again.",
              variant: "destructive",
            });
          }
        }
      } catch (error) {
        // Error checking existing credentials
      } finally {
        setIsCheckingExisting(false);
      }
    };

    checkExistingCredentials();
  }, [encryptedUserId, authLoading, navigate]);

  const [formData, setFormData] = useState<VeracrossFormData>({
    clientId: "",
    clientSecret: "",
    schoolRoute: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingExisting, setIsCheckingExisting] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [existingCredentials, setExistingCredentials] = useState<{
    hasExistingUser: boolean;
    hasValidToken: boolean;
    userInfo: {
      email: string;
      portalId: number;
    };
    tokenInfo?: {
      expiresAt?: Date;
      scopes: string[];
      createdAt: Date;
    };
    existingEncryptedUserId?: string;
    tokenRefreshed?: boolean;
    needsReauth?: boolean;
  } | null>(null);
  const [showExistingCredentialsDialog, setShowExistingCredentialsDialog] = useState(false);

  const formatErrorMessage = (error: string): string => {
    if (error.includes("Not found")) {
      return "The OAuth endpoint was not found. Please verify your School Route is correct.";
    } else if (error.includes("Unauthorized")) {
      return "Invalid Client ID or Client Secret. Please check your credentials.";
    } else if (error.includes("No response received")) {
      return "Unable to connect to Veracross servers. Please check your internet connection and try again.";
    } else if (error.includes("Bad request")) {
      return "Invalid request format. Please check your credentials and School Route.";
    } else if (error.includes("Forbidden")) {
      return "Access denied. Please check if OAuth is enabled for your Veracross instance.";
    }
    return error;
  };

  const handleInputChange = (field: keyof VeracrossFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleUseExistingCredentials = async () => {
    if (!existingCredentials?.existingEncryptedUserId) {
      toast({
        title: "Error",
        description: "No existing credentials found",
        variant: "destructive",
      });
      return;
    }

    setShowExistingCredentialsDialog(false);
    
    navigate(
      `/applicant-field-mapping?userId=${encodeURIComponent(existingCredentials.existingEncryptedUserId)}`
    );
  };

  const handleAddNewCredentials = () => {
    setShowExistingCredentialsDialog(false);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.clientId.trim()) {
      newErrors.clientId = "Client ID is required";
    }

    if (!formData.clientSecret.trim()) {
      newErrors.clientSecret = "Client Secret is required";
    }

    if (!formData.schoolRoute.trim()) {
      newErrors.schoolRoute = "School Route is required";
    } else {
      // Validate school route format (should be alphanumeric and lowercase)
      const schoolRouteRegex = /^[a-z0-9-]+$/;
      if (!schoolRouteRegex.test(formData.schoolRoute.trim())) {
        newErrors.schoolRoute =
          "School Route should contain only lowercase letters, numbers, and hyphens";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!encryptedUserId) {
      toast({
        title: "Error",
        description: "User ID is missing. Please try again.",
        variant: "destructive",
      });
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const cleanedFormData = {
        clientId: formData.clientId.trim(),
        clientSecret: formData.clientSecret.trim(),
        schoolRoute: formData.schoolRoute.trim().toLowerCase(),
      };

      const response = await apiClient.exchangeVeracrossTokens(encryptedUserId, {
        clientId: cleanedFormData.clientId,
        clientSecret: cleanedFormData.clientSecret,
        baseUrl: `https://${cleanedFormData.schoolRoute}.veracross.com`,
      });

      if (response.success) {
        const message =
          response.message ||
          "Veracross credentials validated and saved successfully!";

        const finalUserId = response.finalUserId || encryptedUserId;

        if (response.hasOAuthTokens) {
          toast({
            title: "Success",
            description: message,
          });

          navigate(
            `/applicant-field-mapping?userId=${encodeURIComponent(finalUserId)}`
          );
        } else if (response.oauthError) {
          setErrorMessage(formatErrorMessage(response.oauthError));
          setShowErrorModal(true);
        } else {
          setErrorMessage(
            "Credentials validated but OAuth setup incomplete. Please check your credentials and try again."
          );
          setShowErrorModal(true);
        }
      } else {
        let errorMessage = response.error || "Failed to validate credentials";

        if (errorMessage.includes("Invalid or expired HubSpot token")) {
          errorMessage =
            "Your HubSpot session has expired. Please reconnect your HubSpot account first.";
        } else if (errorMessage.includes("Invalid Veracross credentials")) {
          errorMessage =
            "Invalid Veracross credentials. Please check your Client ID, Client Secret, and School Route.";
        }

        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    } catch (error) {
      let errorMessage = "An error occurred while validating credentials";

      if (error instanceof Error) {
        if (error.message.includes("Failed to fetch")) {
          errorMessage =
            "Unable to connect to the server. Please check your internet connection and try again.";
        } else if (error.message.includes("Invalid or expired HubSpot token")) {
          errorMessage =
            "Your HubSpot session has expired. Please return to the start and re-authenticate with HubSpot.";
        } else if (
          error.message.includes("401") ||
          error.message.includes("403")
        ) {
          errorMessage =
            "Invalid credentials. Please check your Client ID and Client Secret.";
        } else if (error.message.includes("404")) {
          errorMessage =
            "School route not found. Please verify your school route is correct.";
        } else {
          errorMessage = error.message;
        }
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!encryptedUserId) {
    return (
      <div className="w-full">
        <div className="bg-card rounded-lg shadow-sm border p-6">
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">User ID is missing</p>
            <p className="text-muted-foreground">
              Please return to the previous step and try again.
            </p>
            <Button onClick={() => navigate("/")} className="mt-4">
              Return to Start
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isCheckingExisting) {
    return (
      <div className="w-full">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-center py-12">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">
              Checking for existing Veracross credentials...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-foreground mb-4">
          Veracross Credentials
        </h2>
        <p className="text-muted-foreground mb-6">
          Enter your Veracross API credentials to connect your systems.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
          <fieldset disabled={isLoading}>
            <div>
              <label
                htmlFor="clientId"
                className="block text-sm font-medium text-foreground mb-2"
              >
                Client ID
              </label>
              <Input
                id="clientId"
                type="text"
                value={formData.clientId}
                onChange={(e) => handleInputChange("clientId", e.target.value)}
                placeholder="Enter your Veracross Client ID"
                className={errors.clientId ? "border-red-500" : ""}
                autoComplete="off"
                data-form-type="other"
              />
              {errors.clientId && (
                <p className="mt-1 text-sm text-red-600">{errors.clientId}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="clientSecret"
                className="block text-sm font-medium text-foreground mb-2"
              >
                Client Secret
              </label>
              <div className="relative">
                <Input
                  id="clientSecret"
                  type={showClientSecret ? "text" : "password"}
                  value={formData.clientSecret}
                  onChange={(e) =>
                    handleInputChange("clientSecret", e.target.value)
                  }
                  placeholder="Enter your Veracross Client Secret"
                  className={`pr-10 ${errors.clientSecret ? "border-red-500" : ""}`}
                  autoComplete="new-password"
                  data-form-type="other"
                />
                <button
                  type="button"
                  onClick={() => setShowClientSecret(!showClientSecret)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground focus:outline-none transition-colors"
                  tabIndex={-1}
                >
                  {showClientSecret ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.clientSecret && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.clientSecret}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="schoolRoute"
                className="block text-sm font-medium text-foreground mb-2"
              >
                School Route
              </label>
              <Input
                id="schoolRoute"
                type="text"
                value={formData.schoolRoute}
                onChange={(e) =>
                  handleInputChange("schoolRoute", e.target.value)
                }
                placeholder="dwighthanoi"
                className={errors.schoolRoute ? "border-red-500" : ""}
                autoComplete="off"
                data-form-type="other"
              />
              {errors.schoolRoute && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.schoolRoute}
                </p>
              )}
            </div>

            <div className="pt-4">
              <Button type="submit" disabled={isLoading} className="w-full">
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Validating Credentials...
                  </>
                ) : (
                  "Validate & Save Credentials"
                )}
              </Button>
            </div>
          </fieldset>
        </form>

        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <h3 className="text-sm font-medium text-blue-900 mb-2">Need Help?</h3>
          <p className="text-sm text-blue-700 mb-3">
            You can find your Veracross API credentials in your Veracross admin
            panel under API settings or by contacting your Veracross
            administrator.
          </p>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Client ID and Client Secret are provided by Veracross</li>
            <li>
              • School Route is your Veracross subdomain (e.g., dwighthanoi)
            </li>
            <li>• Make sure OAuth is enabled for your Veracross instance</li>
          </ul>
        </div>
      </div>

      {/* Error Modal */}
      <Dialog open={showErrorModal} onOpenChange={setShowErrorModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              OAuth Setup Issue
            </DialogTitle>
            <DialogDescription>{errorMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowErrorModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Existing Credentials Modal */}
              <Dialog open={showExistingCredentialsDialog} onOpenChange={setShowExistingCredentialsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {existingCredentials?.hasValidToken 
                ? "Existing Veracross Credentials Found" 
                : "Existing Veracross Credentials Found (Token Expired)"
              }
            </DialogTitle>
            <DialogDescription>
              We found existing Veracross credentials for your account ({existingCredentials?.userInfo.email}).
              {!existingCredentials?.hasValidToken && " However, your token has expired and cannot be automatically refreshed."}
              Would you like to use the existing credentials or add new ones?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Existing Token Information:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Portal ID: {existingCredentials?.userInfo.portalId}</li>
                <li>• Email: {existingCredentials?.userInfo.email}</li>
                {existingCredentials?.tokenInfo?.expiresAt && (
                  <li>• Expires: {new Date(existingCredentials.tokenInfo.expiresAt).toLocaleDateString()}</li>
                )}
                <li>• Scopes: {existingCredentials?.tokenInfo?.scopes.join(', ') || 'Not available'}</li>
                {existingCredentials?.tokenRefreshed && (
                  <li className="text-green-700 font-medium">• ✅ Token was automatically refreshed</li>
                )}
              </ul>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleAddNewCredentials}>
              Add New Credentials
            </Button>
            <Button onClick={handleUseExistingCredentials}>
              Use Existing Credentials
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VeracrossCredentialsPage;
